import { useState } from 'react';
import { X, AlertCircle, CheckCircle, Eye, Shield, FileText } from 'lucide-react';
import { useApi } from '../../hooks/useApi';

export const ReferralConsentModal = ({ referral, onClose, onSuccess }) => {
    const { put } = useApi();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showDeclineReason, setShowDeclineReason] = useState(false);
    const [declineReason, setDeclineReason] = useState('');

    const handleApprove = async () => {
        try {
            setLoading(true);
            setError(null);

            await put(`/referrals/${referral._id}/student-consent`, {
                approved: true
            });

            onSuccess?.();
            onClose();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to approve referral');
        } finally {
            setLoading(false);
        }
    };

    const handleDecline = async () => {
        try {
            setLoading(true);
            setError(null);

            await put(`/referrals/${referral._id}/student-consent`, {
                approved: false,
                declineReason
            });

            onSuccess?.();
            onClose();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to decline referral');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-gradient-to-r from-uzima-maroon-600 to-uzima-maroon-700 text-white px-6 py-4">
                    <div className="flex justify-between items-start">
                        <div>
                            <h2 className="text-xl font-semibold">Referral Request</h2>
                            <p className="text-blue-100 text-sm mt-1">Your counselor has recommended a referral</p>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-blue-100 hover:text-white p-2 rounded-full hover:bg-blue-500"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                <div className="p-6 space-y-6">
                    {/* Error Message */}
                    {error && (
                        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-r-lg">
                            <div className="flex">
                                <AlertCircle className="h-5 w-5 text-red-400" />
                                <p className="ml-3 text-sm text-red-700">{error}</p>
                            </div>
                        </div>
                    )}

                    {/* From/To */}
                    <div className="bg-gray-50 rounded-lg p-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-xs text-gray-600 mb-1">Current Counselor</p>
                                <p className="font-medium text-gray-900">{referral.referringCounselor?.fullName}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-600 mb-1">Recommended Counselor</p>
                                <p className="font-medium text-gray-900">{referral.targetCounselor?.fullName}</p>
                            </div>
                        </div>
                    </div>

                    {/* New Counselor Info */}
                    <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50">
                        <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                            <Shield className="h-5 w-5 mr-2 text-blue-600" />
                            About Your New Counselor
                        </h3>
                        <div className="space-y-2">
                            <div>
                                <p className="text-sm font-medium text-gray-700">Name</p>
                                <p className="text-gray-900">{referral.targetCounselor?.fullName}</p>
                            </div>
                            {referral.targetCounselor?.staffInfo?.specializations && (
                                <div>
                                    <p className="text-sm font-medium text-gray-700 mb-1">Specializations</p>
                                    <div className="flex flex-wrap gap-2">
                                        {referral.targetCounselor.staffInfo.specializations.map((spec, idx) => (
                                            <span key={idx} className="text-xs bg-blue-200 text-blue-800 px-2 py-1 rounded">
                                                {spec.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {referral.targetCounselor?.staffInfo?.yearsOfExperience && (
                                <div>
                                    <p className="text-sm font-medium text-gray-700">Experience</p>
                                    <p className="text-gray-900">{referral.targetCounselor.staffInfo.yearsOfExperience} years</p>
                                </div>
                            )}
                            {referral.targetCounselor?.staffInfo?.bio && (
                                <div>
                                    <p className="text-sm font-medium text-gray-700">About</p>
                                    <p className="text-gray-900 text-sm">{referral.targetCounselor.staffInfo.bio}</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Reason */}
                    <div>
                        <h3 className="font-semibold text-gray-900 mb-2">Reason for Referral</h3>
                        <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">{referral.reason}</p>
                    </div>

                    {/* Data Sharing Info */}
                    <div className="border-2 border-amber-200 rounded-lg p-4 bg-amber-50">
                        <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                            <Eye className="h-5 w-5 mr-2 text-amber-600" />
                            What Information Will Be Shared?
                        </h3>
                        <p className="text-sm text-gray-700 mb-3">
                            If you approve this referral, the following <strong>partial summary</strong> will be shared with your new counselor:
                        </p>

                        <div className="space-y-3">
                            {referral.partialNotes?.presentingIssues && (
                                <div className="bg-white p-3 rounded border border-amber-200">
                                    <p className="text-xs font-medium text-gray-600 mb-1">Presenting Issues</p>
                                    <p className="text-sm text-gray-800">{referral.partialNotes.presentingIssues}</p>
                                </div>
                            )}

                            {referral.partialNotes?.recommendedFocus && (
                                <div className="bg-white p-3 rounded border border-amber-200">
                                    <p className="text-xs font-medium text-gray-600 mb-1">Recommended Focus</p>
                                    <p className="text-sm text-gray-800">{referral.partialNotes.recommendedFocus}</p>
                                </div>
                            )}

                            {referral.partialNotes?.safetyConsiderations && (
                                <div className="bg-white p-3 rounded border border-amber-200">
                                    <p className="text-xs font-medium text-gray-600 mb-1">Safety Considerations</p>
                                    <p className="text-sm text-gray-800">{referral.partialNotes.safetyConsiderations}</p>
                                </div>
                            )}

                            {referral.partialNotes?.goals && referral.partialNotes.goals.length > 0 && (
                                <div className="bg-white p-3 rounded border border-amber-200">
                                    <p className="text-xs font-medium text-gray-600 mb-1">Session Goals</p>
                                    <ul className="list-disc list-inside text-sm text-gray-800">
                                        {referral.partialNotes.goals.map((goal, idx) => (
                                            <li key={idx}>{goal}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Privacy Assurance */}
                    <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded-r-lg">
                        <div className="flex">
                            <Shield className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
                            <div className="ml-3 text-sm text-green-700">
                                <p className="font-medium">Your Privacy is Protected</p>
                                <ul className="mt-2 space-y-1 list-disc list-inside">
                                    <li>Only the summary above will be shared</li>
                                    <li>Full session notes remain confidential</li>
                                    <li>You can decline without any consequences</li>
                                    <li>You can continue with your current counselor if you prefer</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Decline Reason */}
                    {showDeclineReason && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Reason for Declining (Optional)
                            </label>
                            <textarea
                                value={declineReason}
                                onChange={(e) => setDeclineReason(e.target.value)}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                placeholder="Let us know why you're declining..."
                            />
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-3 pt-4 border-t">
                        {!showDeclineReason ? (
                            <>
                                <button
                                    type="button"
                                    onClick={() => setShowDeclineReason(true)}
                                    className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                                    disabled={loading}
                                >
                                    Decline
                                </button>
                                <button
                                    type="button"
                                    onClick={handleApprove}
                                    disabled={loading}
                                    className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:bg-green-300 flex items-center justify-center gap-2"
                                >
                                    <CheckCircle className="h-5 w-5" />
                                    {loading ? 'Approving...' : 'Approve Referral'}
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    onClick={() => setShowDeclineReason(false)}
                                    className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                                    disabled={loading}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleDecline}
                                    disabled={loading}
                                    className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:bg-red-300"
                                >
                                    {loading ? 'Declining...' : 'Confirm Decline'}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReferralConsentModal;
